import { useEffect, useState } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AgendaItem } from "@pagemates/shared";
import { api, describeError } from "../../lib/api";
import { Avatar, Spinner, EmptyState } from "../../components/ui";
import { Cover } from "../../components/BookBits";
import { Stars } from "../../components/Stars";
import { CheckIcon, LockIcon, QuoteIcon, ChevronIcon } from "../../components/Icons";
import { MeetingCard } from "../../components/MeetingCard";
import { posLabel, type PosCtx } from "../../lib/format";
import { useClub } from "./ClubLayout";
import { PredictionList, PredictionsRow } from "../../components/Moments";
import { ProfileFace } from "../../components/Profile";
import { BackBar } from "../../components/ui";
import { QuestionBox, QuestionRow, refreshQuestions } from "../../components/MeetingQuestions";
import { useClubBooks, useMe, useMembers } from "../../lib/queries";
import { meeting } from "@pagemates/shared/copy";

const txt = meeting.page;

// The meeting page: one phone drives it at the table. Big type, one list, ticks.
/** Readers shown on the book line before the rest fold into "+N" (the full picture is the track on Reading). */
const FACES = 8;

export function MeetingPageView() {
  const { clubId, club } = useClub();
  const c = club.data!;
  const { book: bookParam } = useSearch({ from: "/authed/clubs/$clubId/meeting" });
  const books = useClubBooks(clubId);
  const bookId = bookParam ?? books.data?.current?.id ?? books.data?.finished[0]?.id;
  const m = useQuery({ queryKey: ["meeting", clubId, bookId], queryFn: () => api.meeting(clubId, bookId!), enabled: Boolean(bookId), refetchInterval: 15_000 });
  const detail = useQuery({ queryKey: ["meeting-detail", clubId], queryFn: () => api.meetingDetail(clubId), refetchInterval: 15_000 });
  const me = useMe();
  const members = useMembers(clubId);
  const myNotes = useQuery({ queryKey: ["notes", clubId, bookId], queryFn: () => api.notes(clubId, bookId!), enabled: Boolean(bookId) });
  const preds = useQuery({ queryKey: ["predictions", clubId, bookId, "meeting"], queryFn: () => api.predictions(clubId, bookId!), enabled: Boolean(bookId) });
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [planning, setPlanning] = useState(false); // "Plan your first meeting" opens the form in place
  const [confirm, setConfirm] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: ["meeting", clubId, bookId] });
  const tick = useMutation({
    mutationFn: (i: { item: AgendaItem; done: boolean }) => i.item.kind === "thread"
      ? api.discussComment(clubId, i.item.key, i.done) : api.tickAgenda(clubId, bookId!, i.item.key, i.done),
    onSuccess: refresh,
  });
  const tickQ = useMutation({ mutationFn: (i: { id: string; done: boolean }) => api.discussQuestion(clubId, i.id, i.done), onSuccess: () => refreshQuestions(qc, clubId) });
  const finish = useMutation({
    mutationFn: () => api.setBookStatus(clubId, bookId!, "finished"),
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: ["books", clubId] }); void navigate({ to: "/clubs/$clubId/recap/$bookId", params: { clubId, bookId: bookId! } }); },
  });

  if (books.isPending || (bookId && m.isPending)) return <main className="page"><Spinner /></main>;
  // No book yet: meetings can still be planned. The form opens from one empty card instead of sitting there by default.
  if (!bookId) return (
    <main className="page meeting">
      {c.meeting || planning
        ? <MeetingCard club={c} members={members.data ?? []} isAdmin={c.myRole !== "member"} />
        : c.myRole !== "member"
          ? <EmptyState title={txt.planYourFirstMeeting} action={<button type="button" className="btn primary" onClick={() => setPlanning(true)}>{txt.planAMeeting}</button>}>{txt.pickATimeAnd}</EmptyState>
          : <EmptyState title={txt.noMeetingYet}>{txt.anAdminWillSet}</EmptyState>}
    </main>
  );
  if (m.isError) return <main className="page"><p className="error">{describeError(m.error)}</p></main>;
  const d = m.data!;
  const isAdmin = c.myRole !== "member";
  const finished = d.readers.filter((r) => r.status === "finished");
  const dnf = d.readers.filter((r) => r.status === "dnf");
  const still = d.readers.filter((r) => r.status === "reading");
  const remaining = d.items.filter((i) => !i.done).length;
  const md = detail.data ?? null;
  const cur = books.data?.current ?? null;
  const mine = cur?.progress.find((p) => p.id === me.data?.id);
  const ctx: PosCtx = { pages: d.book.book.pages, audioSeconds: d.book.book.audioSeconds, myFormat: cur?.myFormat, landmarks: cur?.plan?.landmarks.filter((l) => !l.beyond) };
  const qLeft = md ? md.questions.filter((q) => !q.discussed).length : 0;
  // A finished book's page is a look back; the live meeting card belongs to whatever is current.
  const pastBook = d.book.status === "finished" && (cur !== null || c.meeting?.clubBookId !== bookId);
  // A partial stretch keeps the room's agenda to what everyone has read; the rest waits in folds.
  const st = d.stretch;
  const before = st && st.fromPercent > 0 ? d.items.filter((i) => i.position !== null && i.position <= st.fromPercent) : [];
  const after = st && st.toPercent < 100 ? d.items.filter((i) => i.position !== null && i.position > st.toPercent) : [];
  const main = d.items.filter((i) => !before.includes(i) && !after.includes(i));
  const renderItem = (i: AgendaItem) => (
            <div key={i.key} className={`agenda-item row-item${i.done ? " done" : ""}${i.sealed ? " sealed" : ""}`}>
              <button type="button" className="tick" aria-label={i.done ? txt.markNotDiscussed : txt.markDiscussed} aria-pressed={i.done} disabled={tick.isPending} onClick={() => tick.mutate({ item: i, done: !i.done })}>
                {i.done && <CheckIcon width={18} height={18} />}
              </button>
              <div style={{ minWidth: 0 }}>
                {i.kind === "reactions" && !i.sealed ? (
                  <div className="stack-sm">
                    <div className="t">{txt.firstReactions}</div>
                    {d.reactions.map((r) => (
                      <div key={r.id} className="row" style={{ alignItems: "flex-start" }}><Avatar name={r.name} image={r.image} /><div><div className="small muted">{r.name}{r.status === "dnf" ? txt.stoppedAt(r.percent) : ""}</div><div className="book">{r.reaction ? `“${r.reaction}”` : <span className="muted">{txt.noReactionLeft}</span>}</div></div></div>
                    ))}
                  </div>
                ) : i.kind === "predictions" && preds.data?.revealed ? (
                  <div className="stack-sm">
                    <div className="t">{i.title}</div>
                    <PredictionList all={preds.data.all} ctx={ctx} />
                  </div>
                ) : i.kind === "ratings" ? (
                  <div className="stack-sm">
                    <div className="t">{i.title}</div>
                    <div className="row" style={{ flexWrap: "wrap", gap: 10 }}>
                      {d.ratings.members.map((r) => <span key={r.id} className="row small" style={{ gap: 6 }}><Avatar name={r.name} image={r.image} /><Stars value={r.stars} size="sm" />{r.dnf && <span className="pill">{txt.stoppedAt2}{r.stoppedAt}%</span>}</span>)}
                    </div>
                  </div>
                ) : (
                  <>
                    <div className={`t${i.sealed ? " muted" : " book"}`}>{i.sealed ? <span className="row" style={{ gap: 6 }}><LockIcon width={16} height={16} />{i.title}</span> : i.title}</div>
                    <div className="d">
                      {i.detail && <span>{i.detail}</span>}
                      {i.position !== null && i.position > 0 && !i.sealed && <span className="pos">{posLabel(i.position, ctx)}</span>}
                      {i.replyCount ? <span>{i.replyCount} {i.replyCount === 1 ? txt.reply : txt.replies}</span> : null}
                      {i.commentId && !i.sealed && <Link to="/clubs/$clubId/talk" params={{ clubId }} search={{ book: bookId, thread: i.commentId }}>{txt.openThread}</Link>}
                    </div>
                  </>
                )}
              </div>
            </div>
  );

  const readers = d.readers;
  const bookLine = [
    txt.finished2(finished.length),
    dnf.length ? txt.didnTFinish(dnf.length) : "",
    still.length ? txt.stillReading(still.length) : "",
    st && st.toPercent < 100 ? txt.thisMeetingCovers(st.fromPercent > 0 ? txt.to(st.fromPercent) : txt.through, st.toPercent) : "",
  ].filter(Boolean).join(" · ");
  const toGo = qLeft + remaining;

  return (
    <main className="page meeting">
      {pastBook ? (
        <>
          <BackBar label={txt.shelf} link={{ to: "/clubs/$clubId/shelf", params: { clubId } }} />
          <div className="mhero">
            {d.meetingAt && <span className="tile" aria-hidden="true"><i>{new Date(d.meetingAt).toLocaleDateString(undefined, { month: "short" })}</i><b>{new Date(d.meetingAt).getDate()}</b></span>}
            <div className="date">{d.meetingAt ? `${new Date(d.meetingAt).toLocaleDateString(undefined, { weekday: "long", year: "numeric" })} · ${new Date(d.meetingAt).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}` : txt.theMeeting}</div>
          </div>
        </>
      ) : (
        <MeetingCard club={c} members={members.data ?? []} isAdmin={isAdmin} hero />
      )}

      <div className="bookline">
        <Cover book={d.book.book} size="sm" />
        <div style={{ minWidth: 0 }}>
          <div className="t">{d.book.book.title}</div>
          <div className="d">{bookLine}{d.ratings.average !== null ? txt.average(d.ratings.average) : ""}</div>
        </div>
        <div className="who" aria-label={txt.whereEveryoneIs}>
          {readers.slice(0, readers.length > FACES ? FACES - 1 : FACES).map((r) => (
            <span key={r.id} className={`face${r.status === "read_before" ? " before" : ""}`} title={`${r.name} · ${r.status === "finished" ? txt.finished : r.status === "dnf" ? txt.stoppedAt3(r.percent) : r.status === "read_before" ? txt.readItBefore : `${r.percent}%`}`}>
              <ProfileFace user={r} />
              <u>{r.status === "finished" || r.status === "read_before" ? "✓" : `${r.percent}`}</u>
            </span>
          ))}
          {readers.length > FACES && (
            <Link to="/clubs/$clubId" params={{ clubId }} className="face more" title={txt.everyoneOnReading} aria-label={txt.moreReadersOnReading(readers.length - FACES + 1)}>
              <span className="avatar">+{readers.length - FACES + 1}</span>
            </Link>
          )}
        </div>
      </div>

      <div className="mcols">
        <section className="stack-sm agenda-sec">
          <div className="sect"><h3>{txt.agenda}</h3><span className="count">{toGo > 0 ? txt.toGo(toGo) : txt.allDone}</span></div>
          {md && md.questions.length > 0 && (
            <>
              <div className="sub-h">{txt.questionsWeBrought}</div>
              <div className="qlist">{md.questions.map((q) => <QuestionRow key={q.id} clubId={clubId} q={q} ctx={ctx} tick onTick={(done) => tickQ.mutate({ id: q.id, done })} />)}</div>
            </>
          )}
          {(main.length > 0 || before.length > 0 || after.length > 0) && <div className="sub-h">{txt.fromTalk}</div>}
          {d.items.length === 0 && (!md || md.questions.length === 0) && <p className="muted small">{txt.nothingOnItYet}</p>}
          <div className="agenda">{main.map(renderItem)}</div>
          {before.length > 0 && (
            <details className="fold"><summary>{txt.fromLastTime(before.length)}</summary><div className="agenda">{before.map(renderItem)}</div></details>
          )}
          {after.length > 0 && (
            <details className="fold"><summary>{txt.pastThisStretch(after.length)}</summary><div className="agenda">{after.map(renderItem)}</div></details>
          )}
          {preds.data && !preds.data.revealed && <PredictionsRow ctx={ctx} p={preds.data} />}
          {md && <QuestionBox clubId={clubId} herePercent={mine?.percent ?? 0} ctx={ctx} placeholder={txt.addOneOnThe} />}
          {(tick.isError || tickQ.isError) && <p className="error">{describeError(tick.error ?? tickQ.error)}</p>}
        </section>

        <div className="stack-sm maside">
          {/* Closed until opened: the notes are private and the meeting's agenda is what this page is for. The chevron turns when open. */}
          <details className="fold ynotes">
            <summary><span className="eyebrow">{txt.yourNotes(myNotes.data?.length ?? 0)}</span><ChevronIcon className="chev" width={16} height={16} aria-hidden="true" /></summary>
            <div className="ynote-list">
              {myNotes.data?.map((n) => <MyNote key={n.id} n={n} clubId={clubId} bookId={bookId} ctx={ctx} onChange={async () => { await Promise.all([qc.invalidateQueries({ queryKey: ["notes", clubId, bookId] }), refresh()]); }} />)}
              {myNotes.data?.length === 0 && <p className="muted small" style={{ margin: "8px 0 4px" }}>{txt.nothingYetJotOn}</p>}
              <Link to="/clubs/$clubId/notes/$bookId" params={{ clubId, bookId }} className="small nb-open">{txt.openTheNotebook}</Link>
            </div>
          </details>

          {md && <MeetingNotes clubId={clubId} md={md} />}

          {isAdmin && d.book.status === "current" && (
            <div className="stack-sm finish-sec">
              {!confirm ? (
                <>
                  <button className="btn primary" style={{ alignSelf: "flex-start" }} onClick={() => setConfirm(true)}>{txt.finishThisBookFor}</button>
                  <p className="muted small" style={{ margin: 0 }}>{txt.movesItToThe}</p>
                </>
              ) : (
                <div className="confirm">
                  <p className="small">{txt.move}<b>{d.book.book.title}</b>{txt.toFinishedNothingUnseals}</p>
                  <div className="row"><button className="btn primary sm" disabled={finish.isPending} onClick={() => finish.mutate()}>{txt.finishIt}</button><button className="btn ghost sm" onClick={() => setConfirm(false)}>{txt.notYet}</button></div>
                  {finish.isError && <p className="error">{describeError(finish.error)}</p>}
                </div>
              )}
            </div>
          )}
          {d.book.status === "finished" && <p className="muted small">{txt.theClubFinishedThis}<Link to="/clubs/$clubId/recap/$bookId" params={{ clubId, bookId }}>recap</Link>.</p>}
        </div>
      </div>
    </main>
  );
}

/** One of your private notes, on the agenda: read it, or bring it up (share it to Talk, flagged for the meeting). */
function MyNote({ n, clubId, bookId, ctx, onChange }: { n: import("@pagemates/shared").Note; clubId: string; bookId: string; ctx: PosCtx; onChange: () => Promise<unknown> }) {
  const share = useMutation({ mutationFn: () => api.shareNote(clubId, bookId, n.id, true), onSuccess: onChange });
  const unshare = useMutation({ mutationFn: () => api.unshareNote(clubId, bookId, n.id), onSuccess: onChange });
  return (
    <div className={`ynote${n.kind === "quote" ? " is-quote" : ""}`}>
      {n.kind === "quote" && <QuoteIcon width={14} height={14} className="q" />}
      <div className="b">{n.body}</div>
      <div className="m">
        <span className="pos">{n.position === 0 ? txt.open : posLabel(n.position, ctx)}</span>
        {n.sharedCommentId
          ? <><Link to="/clubs/$clubId/talk" params={{ clubId }} search={{ book: bookId, thread: n.sharedCommentId }}>{txt.onTheAgenda}</Link>
              <button type="button" className="linkish small muted" disabled={unshare.isPending} onClick={() => unshare.mutate()}>{txt.takeItBack}</button></>
          : <button type="button" className="linkish small" disabled={share.isPending} onClick={() => share.mutate()}>{share.isPending ? txt.sharing : txt.bringItUp}</button>}
        {(share.isError || unshare.isError) && <span className="error">{describeError(share.error ?? unshare.error) === "has_replies" ? txt.someoneHasRepliedSo : describeError(share.error ?? unshare.error)}</span>}
      </div>
    </div>
  );
}

/** The shared note: one text, host holds the pen by default, anyone can edit, open for a week after the meeting. */
function MeetingNotes({ clubId, md }: { clubId: string; md: NonNullable<Awaited<ReturnType<typeof api.meetingDetail>>> }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(md.notes ?? "");
  useEffect(() => { if (!editing) setDraft(md.notes ?? ""); }, [md.notes, editing]);
  const save = useMutation({ mutationFn: () => api.setMeetingNotes(clubId, draft), onSuccess: async () => { setEditing(false); await qc.invalidateQueries({ queryKey: ["meeting-detail", clubId] }); } });
  const prompts = txt.whatDidPeopleDisagree;
  return (
    <section className={`stack-sm mnote${md.notesSealed ? " sealed" : ""}`}>
      <div className="row spread">
        <span className="eyebrow">{txt.notesFromTheMeeting}</span>
        {!md.notesSealed && <span className="muted small">{md.host ? txt.hasThePen(md.host.name) : txt.anyoneCanEdit}</span>}
      </div>
      {md.notesSealed ? <p className="muted small row" style={{ gap: 6, margin: 0 }}><LockIcon width={14} height={14} />{txt.sealedUntilYouFinish}</p>
        : editing ? (
          <form className="stack-sm" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
            <textarea id="meeting-notes" className="input" rows={6} value={draft} maxLength={8000} placeholder={prompts} onChange={(e) => setDraft(e.target.value)} autoFocus />
            <div className="row"><button type="submit" className="btn primary sm" disabled={save.isPending}>{txt.save}</button><button type="button" className="btn ghost sm" onClick={() => setEditing(false)}>{txt.cancel}</button></div>
            {save.isError && <p className="error small">{describeError(save.error)}</p>}
          </form>
        ) : (
          <>
            {md.notes ? <p className="book notes-text">{md.notes}</p> : <p className="muted small prompt">{prompts}</p>}
            <div className="row small muted" style={{ gap: 10 }}>
              {md.notesBy && md.notesAt && <span>{txt.editedBy(md.notesBy.name, new Date(md.notesAt).toLocaleDateString(undefined, { month: "short", day: "numeric" }))}</span>}
              {md.notesOpen ? <button type="button" className="linkish small" onClick={() => setEditing(true)}>{md.notes ? txt.edit : txt.writeTheNote}</button> : <span>{txt.closedAWeekAfter}</span>}
            </div>
          </>
        )}
    </section>
  );
}
